// Code, plain text, CSV, JSON, Markdown and markup. Decoded here, drawn as
// numbered lines with search and go-to-line, coloured by a highlighter the
// host may supply, validated when it claims to be JSON, gridded when it is
// delimited, and previewed in a sandbox when it is HTML or SVG. Nothing in
// the file ever runs.

import { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState, type ComponentType, type ReactNode } from 'react';
import { Checkbox } from '../../inputs/Checkbox/Checkbox.js';
import { Select } from '../../inputs/Select/Select.js';
import { TextInput } from '../../inputs/TextInput/TextInput.js';
import { SegmentedControl } from '../../inputs/SegmentedControl/SegmentedControl.js';
import { Button } from '../../core/Button/Button.js';
import { copyText } from '@repletoryreach/sdk-utils/clipboard';
import { IconButton, Toolbar, ToolbarGroup, ToolbarText } from '../chrome.js';
import { IconWrap, IconHash } from '../icons.js';
import { decodeText, formatBytes, jsonErrorPosition, parseDelimited } from '../format.js';
import { fill } from '../labels.js';
import type { HighlightProps, RendererProps } from '../types.js';

const MAX_TEXT_BYTES = 4 * 1024 * 1024;
const MAX_LINES = 20000;
const MAX_HIGHLIGHT_BYTES = 200 * 1024;
const MAX_GRID_ROWS = 2000;

const LANGUAGES = [
  'text', 'json', 'jsonc', 'yaml', 'toml', 'xml', 'html', 'css', 'scss', 'javascript', 'typescript', 'jsx', 'tsx',
  'python', 'java', 'csharp', 'cpp', 'c', 'go', 'rust', 'php', 'ruby', 'bash', 'powershell', 'sql', 'graphql',
  'markdown', 'csv', 'ini', 'dotenv', 'docker', 'log'
];

type View = 'raw' | 'rendered' | 'grid';

interface Decoded { text: string; encoding: string; truncated: boolean; totalBytes: number }

export function TextRenderer({ file, detected, labels, loaders, register, announce }: RendererProps): ReactNode {
  const [decoded, setDecoded] = useState<Decoded | null>(null);
  const [wrap, setWrap] = useState(false);
  const [numbers, setNumbers] = useState(true);
  const [language, setLanguage] = useState(detected.language ?? 'text');
  const [view, setView] = useState<View>('raw');
  const [pretty, setPretty] = useState(false);
  const [query, setQuery] = useState('');
  const [matchIndex, setMatchIndex] = useState(0);
  const [gotoValue, setGotoValue] = useState('');
  const codeBox = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    let cancelled = false;
    const blob = file.blob;
    if (!blob) return;
    const slice = blob.size > MAX_TEXT_BYTES ? blob.slice(0, MAX_TEXT_BYTES) : blob;
    decodeText(slice).then(({ text, encoding }) => {
      if (cancelled) return;
      setDecoded({ text, encoding, truncated: blob.size > MAX_TEXT_BYTES, totalBytes: blob.size });
    });
    return () => { cancelled = true; };
  }, [file.blob]);

  const isJson = language === 'json' || language === 'jsonc' || detected.mime === 'application/json';
  const isDelimited = language === 'csv' || language === 'tsv' || /csv|tab-separated/.test(detected.mime);
  const isHtml = language === 'html' || detected.mime === 'text/html';
  const isSvg = detected.mime === 'image/svg+xml';
  const isMarkdown = language === 'markdown';

  const json = useMemo(() => {
    if (!isJson || !decoded) return null;
    try {
      const value = JSON.parse(decoded.text) as unknown;
      return { ok: true as const, pretty: JSON.stringify(value, null, 2) };
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e);
      return { ok: false as const, message, at: jsonErrorPosition(decoded.text, message) };
    }
  }, [isJson, decoded]);

  const shown = useMemo(() => {
    if (!decoded) return '';
    return pretty && json?.ok ? json.pretty : decoded.text;
  }, [decoded, pretty, json]);

  const lines = useMemo(() => (shown.length === 0 ? [] : shown.split('\n')), [shown]);
  const visibleLines = useMemo(() => lines.slice(0, MAX_LINES), [lines]);

  const grid = useMemo(() => {
    if (!isDelimited || !decoded) return null;
    const { rows } = parseDelimited(decoded.text, language === 'tsv' ? '\t' : undefined);
    const columns = rows.reduce((m, r) => Math.max(m, r.length), 0);
    return { rows: rows.slice(0, MAX_GRID_ROWS), columns, total: rows.length };
  }, [isDelimited, decoded, language]);

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [] as number[];
    const out: number[] = [];
    for (let i = 0; i < visibleLines.length; i++) if (visibleLines[i].toLowerCase().includes(q)) out.push(i);
    return out;
  }, [query, visibleLines]);

  useEffect(() => { setMatchIndex(0); }, [query]);

  const scrollToLine = useCallback((n: number) => {
    const el = codeBox.current?.querySelector<HTMLElement>(`[data-line="${n}"]`);
    el?.scrollIntoView({ block: 'center' });
  }, []);

  useEffect(() => {
    if (matches.length > 0) scrollToLine(matches[matchIndex] + 1);
  }, [matches, matchIndex, scrollToLine]);

  const stepMatch = (dir: 1 | -1) => {
    if (matches.length === 0) return;
    setMatchIndex((i) => (i + dir + matches.length) % matches.length);
  };

  const goTo = () => {
    const n = Number(gotoValue);
    if (!Number.isFinite(n) || n < 1) return;
    scrollToLine(Math.min(n, visibleLines.length));
  };

  const copyAll = useCallback(async () => {
    const r = await copyText(shown);
    announce(r.kind === 'ok' ? labels.copied : labels.copyFailed);
    return r.kind === 'ok';
  }, [shown, announce, labels]);

  useEffect(() => {
    register({
      capabilities: { search: true, copyText: true, fullscreen: true, print: true },
      actions: {
        copyText: copyAll,
        print: () => {
          const w = window.open('', '_blank', 'noopener,width=900,height=700');
          if (!w) return;
          w.document.write(`<!doctype html><title>${escapeHtml(file.name)}</title><style>body{font:12px/1.5 ui-monospace,Menlo,monospace;white-space:pre-wrap;word-break:break-word;margin:24px}</style><pre>${escapeHtml(shown)}</pre><script>window.print()</script>`);
          w.document.close();
        }
      },
      details: decoded ? [
        { label: labels.detailLines, value: String(lines.length) },
        { label: labels.detailEncoding, value: decoded.encoding },
        { label: labels.detailLanguage, value: language },
        ...(json ? [{ label: labels.detailValidation, value: json.ok ? labels.validJson : fill(labels.invalidJson, { line: json.at?.line ?? '?', column: json.at?.column ?? '?', message: json.message }) }] : []),
        ...(grid ? [{ label: labels.viewGrid, value: fill(labels.gridSummary, { rows: grid.total, columns: grid.columns }) }] : []),
        { label: labels.detailRenderer, value: labels.rendererNative }
      ] : [],
      status: decoded ? `${lines.length} ${labels.detailLines.toLowerCase()} · ${decoded.encoding}${json ? ` · ${json.ok ? labels.validJson : labels.detailValidation}` : ''}` : undefined,
      onKey: (e) => {
        if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'f') {
          const input = codeBox.current?.parentElement?.querySelector<HTMLInputElement>('input[data-fv-search]');
          if (input) { input.focus(); input.select(); return true; }
        }
        if (e.key === '/') {
          const input = codeBox.current?.parentElement?.querySelector<HTMLInputElement>('input[data-fv-search]');
          if (input) { input.focus(); return true; }
        }
        return false;
      }
    });
  }, [register, copyAll, decoded, lines.length, language, json, grid, labels, shown, file.name]);

  const Highlight = useMemo<ComponentType<HighlightProps> | null>(() => {
    if (!loaders?.highlight) return null;
    const load = loaders.highlight;
    return lazy(() => load().then((m) => ({ default: m.CodeHighlight })).catch(() => ({ default: PlainFallback })));
  }, [loaders]);

  if (!decoded) return <div className="rr-fv__canvas rr-fv__canvas--text" />;

  const highlightable = !!Highlight && language !== 'text' && language !== 'log' && decoded.totalBytes <= MAX_HIGHLIGHT_BYTES && !wrap;
  const previewable = isHtml || isSvg;
  const canGrid = !!grid;

  const viewOptions = [
    { label: labels.viewRaw, value: 'raw' },
    ...(previewable ? [{ label: labels.viewRendered, value: 'rendered' }] : []),
    ...(canGrid ? [{ label: labels.viewGrid, value: 'grid' }] : [])
  ];

  return (
    <div className="rr-fv__renderer">
      <Toolbar>
        <ToolbarGroup>
          <Select aria-label={labels.language} value={language} onChange={(e) => setLanguage(e.currentTarget.value)}
            data={LANGUAGES.includes(language) ? LANGUAGES : [language, ...LANGUAGES]} />
        </ToolbarGroup>
        <ToolbarGroup className="rr-fv__toolbar-group--grow">
          <TextInput
            data-fv-search=""
            aria-label={labels.searchInFile}
            placeholder={labels.searchInFile}
            value={query}
            onChange={(e) => setQuery(e.currentTarget.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); stepMatch(e.shiftKey ? -1 : 1); } }}
            className="rr-fv__search"
          />
          <ToolbarText mono>{query ? (matches.length ? fill(labels.searchMatches, { current: matchIndex + 1, total: matches.length }) : labels.searchNone) : ''}</ToolbarText>
          <TextInput
            aria-label={labels.goToLine}
            placeholder={labels.goToLine}
            value={gotoValue}
            inputMode="numeric"
            onChange={(e) => setGotoValue(e.currentTarget.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); goTo(); } }}
            className="rr-fv__goto"
          />
        </ToolbarGroup>
        <ToolbarGroup>
          <IconButton label={labels.wrapLines} pressed={wrap} active={wrap} onClick={() => setWrap((v) => !v)}><IconWrap /></IconButton>
          <IconButton label={labels.lineNumbers} pressed={numbers} active={numbers} onClick={() => setNumbers((v) => !v)}><IconHash /></IconButton>
          {json?.ok && <Checkbox size="sm" label={labels.prettyPrint} checked={pretty} onChange={setPretty} />}
        </ToolbarGroup>
        {viewOptions.length > 1 && (
          <ToolbarGroup>
            <SegmentedControl size="xs" value={view} onChange={(v) => setView(v as View)} data={viewOptions} />
          </ToolbarGroup>
        )}
        <ToolbarGroup>
          <Button size="xs" variant="default" onClick={() => void copyAll()}>{labels.copyCode}</Button>
        </ToolbarGroup>
      </Toolbar>

      {json && !json.ok && (
        <div className="rr-fv__banner rr-fv__banner--warning" role="status">
          {fill(labels.invalidJson, { line: json.at?.line ?? '?', column: json.at?.column ?? '?', message: json.message })}
        </div>
      )}
      {decoded.truncated && (
        <div className="rr-fv__banner" role="status">{fill(labels.truncatedNote, { shown: formatBytes(MAX_TEXT_BYTES), total: formatBytes(decoded.totalBytes) })}</div>
      )}
      {!decoded.truncated && lines.length > MAX_LINES && (
        <div className="rr-fv__banner" role="status">{fill(labels.truncatedNote, { shown: `${MAX_LINES} ${labels.detailLines.toLowerCase()}`, total: `${lines.length} ${labels.detailLines.toLowerCase()}` })}</div>
      )}
      {isMarkdown && <div className="rr-fv__banner" role="status">{labels.noRendererNote}</div>}

      {view === 'rendered' && previewable ? (
        <div className="rr-fv__canvas rr-fv__canvas--preview">
          {isSvg && file.objectUrl
            ? <img className="rr-fv__svg-preview" src={file.objectUrl} alt={file.name} />
            : <iframe className="rr-fv__sandbox" sandbox="" title={file.name} srcDoc={withCsp(decoded.text)} />}
          <div className="rr-fv__canvas-note">{labels.sandboxNote}</div>
        </div>
      ) : view === 'grid' && grid ? (
        <div className="rr-fv__canvas rr-fv__canvas--grid">
          <table className="rr-fv__grid">
            <thead>
              <tr><th className="rr-fv__grid-num" /> {Array.from({ length: grid.columns }, (_, i) => <th key={i}>{columnName(i)}</th>)}</tr>
            </thead>
            <tbody>
              {grid.rows.map((row, r) => (
                <tr key={r}>
                  <td className="rr-fv__grid-num">{r + 1}</td>
                  {Array.from({ length: grid.columns }, (_, c) => <td key={c}>{row[c] ?? ''}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
          <div className="rr-fv__canvas-note">{fill(labels.gridSummary, { rows: grid.total, columns: grid.columns })}</div>
        </div>
      ) : (
        <div ref={codeBox} className={['rr-fv__canvas', 'rr-fv__canvas--text', wrap && 'rr-fv__canvas--wrap', numbers && 'rr-fv__canvas--numbers'].filter(Boolean).join(' ')}>
          {shown.length === 0 ? (
            <div className="rr-fv__inline-note">{labels.emptyText}</div>
          ) : highlightable && Highlight ? (
            <Suspense fallback={<PlainLines lines={visibleLines} numbers={numbers} matches={matches} current={matchIndex} />}>
              <Highlight code={visibleLines.join('\n')} language={language} withLineNumbers={numbers} withCopyButton={false} className="rr-fv__highlight" />
            </Suspense>
          ) : (
            <PlainLines lines={visibleLines} numbers={numbers} matches={matches} current={matchIndex} />
          )}
        </div>
      )}
    </div>
  );
}

function PlainLines({ lines, numbers, matches, current }: { lines: string[]; numbers: boolean; matches: number[]; current: number }): ReactNode {
  const hit = new Set(matches);
  const active = matches[current];
  return (
    <pre className="rr-fv__pre">
      {lines.map((l, i) => (
        <span key={i} data-line={i + 1} className={['rr-fv__line', hit.has(i) && 'rr-fv__line--hit', i === active && 'rr-fv__line--current'].filter(Boolean).join(' ')}>
          {numbers && <span className="rr-fv__ln" aria-hidden>{i + 1}</span>}
          <span className="rr-fv__lc">{l.length === 0 ? ' ' : l}</span>
        </span>
      ))}
    </pre>
  );
}

function PlainFallback({ code }: HighlightProps): ReactNode {
  return <pre className="rr-fv__pre"><span className="rr-fv__lc">{code}</span></pre>;
}

function columnName(i: number): string {
  let s = '';
  let n = i;
  do { s = String.fromCharCode(65 + (n % 26)) + s; n = Math.floor(n / 26) - 1; } while (n >= 0);
  return s;
}

/** A Content Security Policy in the document itself, so the sandbox also blocks outside requests. */
function withCsp(html: string): string {
  const csp = `<meta http-equiv="Content-Security-Policy" content="default-src 'none'; img-src data:; style-src 'unsafe-inline'; font-src data:">`;
  if (/<head[^>]*>/i.test(html)) return html.replace(/<head[^>]*>/i, (m) => `${m}${csp}`);
  return `${csp}${html}`;
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string));
}
